import React from 'react';
import {camelCaseObject, getPtDescriptionByCode, mapMessages, toLabel, sendGoogleAnalyticsEvent} from '../lib/utils';
import {$, moment, showSpinner, stopSpinner} from '../lib/ext-refs';
import TextField from '../lib/mdb/text-field';
import Selectize from '../lib/mdb/selectize';
import {errorToFieldMap, handleUnauthorizedRequest} from '../lib/payment-utils';
import _get from 'lodash/get';

/**
 * Renders the account information section for a payment.
 *
 * Expected parameters
 * idx - index of the payment
 * payment
 * onValidAccount - function to call after account validation passes
 * onRemove - function to call when the garbage bin icon is clicked
 * onCancel - function to call when the Cancel Payment button is clicked
 * showRemove - flag to show the garbage bin icon or not
 * active - true if the account is valid
 * state
 * fieldPrefix - 'payments[idx].header'
 */
class AccountInfo extends React.Component {
    constructor(props) {
        super(props);
        // setup notifications...
        this.removeAccount = this.removeAccount.bind(this);
        this.addAccount = this.addAccount.bind(this);
        this.cancelPayment = this.cancelPayment.bind(this);
        this.paymentTypePostProcess = this.paymentTypePostProcess.bind(this);
        this.setGenericInvalidAccountError = this.setGenericInvalidAccountError.bind(this);
        this.onValidAccountPostProcess = this.onValidAccountPostProcess.bind(this);
    }

    componentDidMount() {
        const {payment, idx, state} = this.props,
            {header} = payment,
            {preAddAccount} = header;
        if(preAddAccount){
            this.addAccount(this);
            state.setValues((data) => {
                data.payments[idx].header.preAddAccount = false;
                return data;
            });
        }
    }

    removeAccount(e) {
        const payment = this.props.payment;
        // notify parent this account is removed
        this.props.onRemove(payment);
    }

    cancelPayment(e) {
        this.props.onCancel();
    }

    addAccount(e) {
        const {payment, onValidAccount, state, idx, fieldPrefix, appComponents} = this.props,
            {config, data} = state,
            {xotpJwtToken} = data,
            {xotpUser, messages} = config;

        sendGoogleAnalyticsEvent('Payment', messages["ga.event.action.addAccount"] || 'Clicked Add Account', config.gaEventLabel);

        // reset the session timeout warning, set the flag to true so that session timeout warning will extend 
        // when add an account in AD.
		state.setValue('resetSessionTimeoutWarning', true);
        
        const {buildAddAccountAjaxUrl} = appComponents;

        const {preAccountAjaxLookupValidation} = appComponents;

        if(!preAccountAjaxLookupValidation(payment, fieldPrefix, state)){
            return;
        }


        $('.spinner-text').html('Loading');
        showSpinner(180000);

        // URL, token ...
        // do an Ajax call, and update state when received answer
        const url = buildAddAccountAjaxUrl(payment, config, data);
        $.ajax({
                method: 'GET',
                url: url,
                username: xotpUser,
                password: "",
                headers: {
                    "jwt": xotpJwtToken
                },
                dataType: "json",
                success: (data) => {
                    data = camelCaseObject(data) || {};
                    data = data.accountInfoResponse || {};
                    let validationStatus = data.validationStatus;
                    if (validationStatus === 'PASSED') {
                        payment.accountInfoResponse = data;
                        if(payment.accountInfoResponse.bundle){
                            payment.bundle = payment.accountInfoResponse.bundle;
                        }
                        onValidAccount(payment);
                        this.onValidAccountPostProcess(payment);
                    } else {
                        // show errors
                        if (!data.errors || !data.errors.error) {
                            // if no errors from the response, show a generic account error
                            this.setGenericInvalidAccountError();
                        } else {
                            // map backend errors to fields in the UI
                            let errors = data.errors.error;
                            errors = !Array.isArray(errors) ? [errors] : errors;
                            let mappedErrors = {};
                            errors.forEach(error => {
                                const {key, value} = error,
                                    keyPrefix = key.substring(0, key.indexOf('.')),
                                    fieldName = `payments[${idx}].${errorToFieldMap[keyPrefix]}`;
                                mappedErrors[fieldName] = value;
                            });
                            state.setErrors(mappedErrors);
                        }
                    }
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    console.log('Error: ', jqXHR, textStatus, errorThrown);
                    if (jqXHR.status === 401) {
                        handleUnauthorizedRequest();
                        return;
                    }
                    // show errors in the account number field
                    this.setGenericInvalidAccountError();
                },
                complete: (xhr, status) => {
                    stopSpinner();
                    if (status === 'timeout') {
                        // show errors in the account number field
                        this.setGenericInvalidAccountError();
                    }
                }
            }
        );
    }

    onValidAccountPostProcess(payment) {
        // nothing by default, override if you need to customize behavior
    }

    setGenericInvalidAccountError() {
        const {state, fieldPrefix} = this.props;
        state.setErrors({
            [`${fieldPrefix}.accountNumber`]: 'Account not found'
        });
    }

    paymentTypePostProcess(name, value, data) {
        const {payment, state, appComponents} = this.props;
        const {paymentTypePostProcess} = appComponents;

        if (paymentTypePostProcess && typeof paymentTypePostProcess === 'function') {
            paymentTypePostProcess(name, value, data, state, payment);
            return;
        }

        const accountNumberField = `${name.substring(0, name.lastIndexOf('.'))}.accountNumber`,
            accountNumber = _get(data, accountNumberField);
        // revalidate the account number based on the new payment type
        state.resetError(accountNumberField);
        if (accountNumber) {
            state.validationState[accountNumberField] = state.validateField(accountNumberField, accountNumber);
        }

    }

    render() {
        const {
            payment, idx, state, fieldPrefix, showIcon, onValidAccount,
            appComponents
        } = this.props;
        const {config, validationState} = state;
        const {messages} = config;

        // check if we have errors for this payment, if yes then disable the Add button
        let disableAddButton = Object.keys(validationState).find(key => key.startsWith(fieldPrefix) && validationState[key]);

        const {
            AccountInput,
            AccountDisplay,
            AccountInfoButtons,
            AccountEntryHelpModal
        } = appComponents;

        const titleText = messages["otp.accountInfo.title.alt"] ? messages["otp.accountInfo.title.alt"] : messages["otp.accountInfo.title"] + ' #' + (idx + 1);

        return (
            <div className="opp-section" id={`AccountInfo${idx + 1}`}>
                <h3 className="section-title">
                    {showIcon && (
                        <span className="section-icon">
                        <img src={messages["opp.icon-account-info"] || "images/icon-account-information.png"}
                             alt="Account Information"/>
                        </span>)
                    }
                    <span className="section-text">{titleText}</span>

                    <div className="btn-group">
                        <button
                            type="button"
                            className="btn btn-transparent px-3 waves-effect waves-light remove-account-icon"
                            style={this.props.showRemove ? {display: "inline-block"} : {}}
                            title={messages["opp.removeAccount.label"]}
                            onClick={this.removeAccount}>
                            <i className="material-icons">{messages["icon.material.delete.label"]}</i>
                        </button>
                    </div>
                </h3>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-v2">
                            <div className="card-body">
                                <div className="row opp-account-rows">
                                    <AccountNotes
                                        key={"AccountNote" + idx}
                                        config={config}
                                    />
                                    {payment.active
                                        ? <AccountDisplay
                                            config={config}
                                            payment={payment}
                                        />
                                        : <AccountInput
                                            key={"AccountInput" + idx}
                                            idx={idx}
                                            config={config}
                                            state={state}
                                            payment={payment}
                                            fieldPrefix={fieldPrefix}
                                            paymentTypePostProcess={this.paymentTypePostProcess}
                                            onValidAccount={onValidAccount}
                                        />
                                    }
                                    {
                                    <AccountNotesSideCard
                                        key={"AccountNoteSide" + idx}
                                        config={config}
                                    />
                                    }
                                </div>
                            </div>
                            <AccountInfoButtons
                                payment={payment} state={state}
                                addAccount={this.addAccount} cancelPayment={this.cancelPayment}
                                disableAddButton={disableAddButton}
                            />
                        </div>
                    </div>
                </div>
                <AccountEntryHelpModal
                    config={config}
                />
            </div>
        );
    }
}

const AccountInfoButtons = ({payment, state, cancelPayment, addAccount, disableAddButton}) => {
    const {config} = state;
    const {messages} = config;
    return (
        <div className="card-footer flex-footer">
            <button
                type="button"
                className="btn btn-primary btn-account-cancel ml-1 mr-1"
                data-toggle="modal"
                data-target="#modal-cancel-payment-confirm"
                onClick={cancelPayment}
                disabled={!payment.active}
            >
                {messages["opp.cancel.payment.button.label"]}
            </button>
            {!payment.active && (
                <button
                    type="button"
                    className="btn btn-primary waves-effect waves-light btn-validate-account ml-1 mr-1"
                    onClick={addAccount}
                    disabled={disableAddButton}
                >
                    {messages["add.label"]}
                </button>
            )}
        </div>
    );
};

const preAccountAjaxLookupValidation = (payment, fieldPrefix, state, allowDuplicateAccounts = false) => {
    const messages = state.config.messages;

    // make sure we have payment type before submitting
    if (!payment.header.paymentTypeCode) {
        const fieldName = `${fieldPrefix}.paymentTypeCode`;
        state.setErrors({
            [fieldName]: messages["opp.validation.pt.invalid"]
        });
        return false;
    }

    if (allowDuplicateAccounts === false) {
        const payments = state.data.payments;
        // Check if the current account is already in the list, and if so, skip it
        const acct = payment.header.accountNumber;
        const duplicate = payments.find(p => p.header.accountNumber === acct);
        if (duplicate !== payment) {
            const fieldName = `${fieldPrefix}.accountNumber`;
            state.setErrors({
                [fieldName]: messages["opp.validation.acct.duplicate"]
            });
            return false;
        }
    }

    return true;
}


const buildAddAccountAjaxUrl = (payment, config, data) => {
    const {header} = payment;
    const {nginxJwtToken} = data;
    const {paymentConfig, channelCode, tla} = config;

    let accountSearchFields = '';
    accountSearchFields += "account-number=" + encodeURIComponent(header.accountNumber || '') +
        "&payment-type-code=" + encodeURIComponent(header.paymentTypeCode || '') +
        "&auth-token1=" + encodeURIComponent(header.authToken1 || '') +
        "&auth-token2=" + encodeURIComponent(header.authToken2 || '') +
        "&auth-token3=" + encodeURIComponent(header.authToken3 || '');

    if ((channelCode === "AD" && paymentConfig.payWithPreviousPmSupportedAd) || (channelCode !== "AD" && paymentConfig.payWithPreviousPmSupported)) {
        accountSearchFields += "&include-last-used-pm=true"
    }
    accountSearchFields += `&channel-code=${channelCode}`;

    // Add JWT for ms validation by nginx
    accountSearchFields += "&JWT_TOKEN=" + encodeURIComponent(nginxJwtToken);

    // Add timestamp to ensure cached page is not returned
    accountSearchFields += `&ts=${moment().unix()}`;

    return `./xotp-api/accounts/${tla}?${accountSearchFields}`;

};

const AccountNotes = ({config}) => {
    const {messages} = config;
    const msgList = mapMessages(messages, "opp.accountNotesCardTop.msg", 5),
        hasNotes = msgList.length > 0;
    if (!hasNotes) {
        return null;
    }
    return (
        <div className="col-12 mb-4">
            <div className="card card-minimal">
                <h5 className="card-title mb-0">
                    {messages["opp.accountNotesCardTop.title"]}
                </h5>
                <div className="card-body">
                    <ul className="list-unstyled mb-0">
                        {msgList.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                </div>
            </div>
        </div>
    );
};

const AccountNotesSideCard = ({config}) => {
    const {messages} = config,
        msgList = mapMessages(messages, "opp.accountNotesCardSide.msg", 5),
        hasNotes = msgList.length > 0;
    if (!hasNotes) {
        return null;
    }
    return (
        <div className="col-md-6 col-xs-12">
            <div className="card card-minimal mt-4 m-sm-0 ">
                <h5 className="card-title mb-0">
                    {messages["opp.accountNotesCardSide.title"]}
                </h5>
                <div className="card-body">
                    <ul className="mb-0">
                        {msgList.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                </div>
            </div>
        </div>
    );
};

/**
 * Display account information like payment type, account number
 * and auth tokens
 * @param config
 * @param payment
 * @returns {*}
 * @constructor
 */
const AccountDisplay = ({config, payment}) => {
    const {paymentConfig, messages} = config;
    const {header} = payment;
    const {liabilityToken1Enabled, liabilityToken2Enabled, liabilityToken3Enabled} = paymentConfig;
    return (
        <div className="col-md-6 col-12 mt-4">
            <React.Fragment>
                <div className="row">
                    <div className="col-12">
                        <span className="display-label">{messages["payment.type.label"]}</span>
                        <span className="display-plaintext">
                    {getPtDescriptionByCode(paymentConfig, header.paymentTypeCode)}</span>
                    </div>
                </div>
                <div className="row mt-5">
                    <div className="col-12">
                        <span className="display-label">{messages["payment.account.label"]}</span>
                        <span className="display-plaintext">{header.accountNumber}</span>
                    </div>
                </div>
                {liabilityToken1Enabled && (
                    <div className="row mt-5">
                        <div className="col-12">
                                <span
                                    className="display-label">{toLabel(messages["otp.authToken1.label"])}</span>
                            <span className="display-plaintext">{header.authToken1}</span>
                        </div>
                    </div>
                )}
                {liabilityToken2Enabled && (
                    <div className="row mt-5">
                        <div className="col-12">
                                <span
                                    className="display-label">{toLabel(messages["otp.authToken2.label"])}</span>
                            <span className="display-plaintext">{header.authToken2}</span>
                        </div>
                    </div>
                )}
                {liabilityToken3Enabled && (
                    <div className="row mt-5">
                        <div className="col-12">
                                <span
                                    className="display-label">{toLabel(messages["otp.authToken3.label"])}</span>
                            <span className="display-plaintext">{header.authToken3}</span>
                        </div>
                    </div>
                )}
            </React.Fragment>
        </div>
    );
};


const AccountInput = ({config, idx, payment, fieldPrefix, paymentTypePostProcess}) => {
    const {paymentConfig, messages} = config,
        {
            accountPatternsWithPtId, liabilityToken1Enabled, liabilityToken2Enabled, liabilityToken3Enabled,
            liabilityToken1Size, liabilityToken2Size, liabilityToken3Size,
            liabilityToken1Config, liabilityToken2Config, liabilityToken3Config,
            authToken1Optional, authToken2Optional, authToken3Optional,
            defaultPaymentTypeToLiability
        } = paymentConfig,
        {header} = payment,
        authToken1Validations = {},
        authToken2Validations = {},
        authToken3Validations = {};

    if (!authToken1Optional) {
        authToken1Validations.required = messages["authToken1.invalid"];
    }
    if (!authToken2Optional) {
        authToken2Validations.required = messages["authToken2.invalid"];
    }
    if (!authToken3Optional) {
        authToken3Validations.required = messages["authToken3.invalid"];
    }

    if (liabilityToken1Config === "ZIP") {
        authToken1Validations.zipCode = messages["authToken1.invalid"];
    }
    if (liabilityToken2Config === "ZIP") {
        authToken2Validations.zipCode = messages["authToken2.invalid"];
    }
    if (liabilityToken3Config === "ZIP") {
        authToken3Validations.zipCode = messages["authToken3.invalid"];
    }

    return (
        <div className="col-md-6 col-12">
            {!defaultPaymentTypeToLiability &&
            <PaymentTypeSelect
                fieldPrefix={fieldPrefix}
                config={config}
                postProcess={paymentTypePostProcess}/>
            }
            <AccountNumberInput
                fieldPrefix={fieldPrefix}
                payment={payment}
                config={config}/>
            {liabilityToken1Enabled && (
                <TextField
                    key="AuthToken1"
                    name={fieldPrefix + '.authToken1'}
                    className="md-form form-group mb-0 mt-5"
                    description={toLabel(messages["otp.authToken1.label"])}
                    validations={authToken1Validations}
                    autoComplete="off"
                    maxLength={liabilityToken1Size}
                    aria-required="true"
                />)
            }
            {liabilityToken2Enabled && (
                <TextField
                    key="AuthToken2"
                    name={fieldPrefix + '.authToken2'}
                    className="md-form form-group mb-0 mt-5"
                    description={toLabel(messages["otp.authToken2.label"])}
                    validations={authToken2Validations}
                    autoComplete="off"
                    maxLength={liabilityToken2Size}
                    aria-required="true"
                />)
            }
            {liabilityToken3Enabled && (
                <TextField
                    key="AuthToken3"
                    name={fieldPrefix + '.authToken3'}
                    className="md-form form-group mb-0 mt-5"
                    description={messages["otp.authToken3.label"]}
                    validations={authToken3Validations}
                    autoComplete="off"
                    maxLength={liabilityToken3Size}
                    aria-required="true"
                />)
            }
        </div>
    );
};

const PaymentTypeSelect = ({fieldPrefix, config, paymentTypes, postProcess}) => {
    const {paymentConfig, messages} = config;
    const ptList = paymentTypes || paymentConfig.paymentTypes;
    const emptyValue = messages["select.label"];

    return (
        <Selectize
            key="PaymentType"
            name={fieldPrefix + '.paymentTypeCode'}
            description={messages["payment.type.label"]}
            selectizeOptions={{
                dropdownParent: "body",
                highlight: false
            }}
            selectList={ptList}
            autoComplete="off"
            validations={{
                required: messages["paymentTypeCode.empty"]
            }}
            postProcess={postProcess}>
            <option key="Empty" value="">{emptyValue}</option>
        </Selectize>
    );
};

const AccountNumberInput = ({fieldPrefix, payment, config}) => {
    const {paymentConfig, messages} = config,
        {accountPatternsWithPtId} = paymentConfig,
        {header} = payment,
        accountHelpMessageTitle = messages["opp.accountNumberHelp.title"];
    return (
        <TextField
            key="AccountNumber"
            name={fieldPrefix + '.accountNumber'}
            className="md-form form-group mb-0 mt-5"
            labelClass="has-popover"
            description={() => (
                <React.Fragment>
                    {messages["payment.account.label"]}
                    {accountHelpMessageTitle && (
                        <a href="#" data-toggle="modal" data-target="#modal-account-help">
                            <i className="material-icons">open_in_new</i></a>
                    )}
                </React.Fragment>
            )}
            validations={{
                required: messages["payment.accountNumber.required"],
                notContainsCreditCardNumber: messages["input.invalidCC"],
                accountPatterns: (value, state) => {
                    if (!value) {
                        return '';
                    }
                    let accountPatterns = [];
                    if (accountPatternsWithPtId) {
                        const paymentTypeCode = _get(state, fieldPrefix + '.paymentTypeCode');
                        const ptPattern = accountPatternsWithPtId.find(ptPattern => ptPattern.code === paymentTypeCode);
                        if (ptPattern) {
                            accountPatterns = accountPatterns.concat(ptPattern.patterns);
                        }
                    }
                    //TODO search for non payment type specific account patterns and append them to the array
                    if (!accountPatterns) {
                        return '';
                    }

                    let error = '';
                    accountPatterns.forEach(pattern => {
                        let patt = new RegExp(pattern);
                        let res = patt.test(value);
                        if (!res) {
                            error = messages["payment.accountNumber.invalid"];
                        }
                    });
                    return error;
                }
            }}
            autoComplete="off"
            maxLength="32"
            aria-required="true"
        />
    )
};


const AccountEntryHelpModal = ({config}) => {
    const {messages} = config,
        msgList = mapMessages(messages, "opp.accountNumberHelp.msg", 5);
    return (
        <div className="modal mdb-modal fade" id="modal-account-help" tabIndex="-1" role="dialog"
             aria-labelledby="modal-account-help" aria-hidden="true">
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {messages["opp.accountNumberHelp.title"]}
                        </h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true"><i className="material-icons">&#xE5CD;</i></span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-12">
                                {msgList.map((msg, index) => {
                                    if (index === 0) return (
                                        <p key={"account-help-msg-" + index}><b key={"account-help-msgb-" + index}>{msg}</b></p>);
                                    return (<p key={"account-help-msg-" + index}>{msg}</p>);
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn btn-primary" data-dismiss="modal">
                            {messages["closeButton.label"]}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};



const AccountAddSection = ({onAdd, state}) => {
    const {config} = state, 
        {messages} = config;
    
    return (
        <div className="opp-section add-another-section">
            <div className="row">
                <div className="col-12">
                    <button
                        type="button"
                        className="btn btn-primary btn-add-account mr-0 text-capitalize waves-effect waves-light"
                        onClick={onAdd}>
                        <i className="material-icons">{messages["icon.material.add.label"]}</i>
                        {messages["opp.addAnotherAccount.label"]}
                    </button>
                </div>
            </div>
        </div>
    );
};

export {
    AccountInfo,
    AccountAddSection,
    AccountNotes,
    AccountInput,
    AccountNumberInput,
    PaymentTypeSelect,
    AccountInfoButtons,
    AccountDisplay,
    buildAddAccountAjaxUrl,
    preAccountAjaxLookupValidation,
    AccountEntryHelpModal
};
